import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, ShoppingBag, ArrowRight, Info, Tag, Check } from "lucide-react";
import { useCart } from "../context/CartContext";
import { Price } from "../components/Price";
import { useQuote } from "../hooks/useQuote";

export default function Cart() {
  const { items, remove, setQty } = useCart();
  const navigate = useNavigate();
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState("");
  const { quote } = useQuote(items, "Greater Accra", promo);

  const fx = quote?.fx_gbp_ghs || 19.5;
  const gh = (n) => `GH₵ ${Math.round(Number(n) || 0).toLocaleString()}`;

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-24 text-center">
        <div className="w-16 h-16 rounded-full bg-[#E7E1B1] flex items-center justify-center mx-auto mb-6">
          <ShoppingBag size={26} className="text-[#5C5C5C]" />
        </div>
        <h1 className="font-display text-3xl mb-3">Your cart is empty</h1>
        <p className="text-[#5C5C5C] mb-8">Discover authentic UK products delivered to Ghana.</p>
        <Link to="/shop" data-testid="cart-shop-cta" className="inline-flex items-center gap-2 bg-[#306D29] text-white font-semibold px-7 py-3.5 rounded-full">
          Shop UK <ArrowRight size={18} />
        </Link>
      </div>
    );
  }

  const applied = quote && quote.discount_ghs > 0 && quote.promo_code;

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
      <h1 className="font-display text-4xl tracking-tight mb-8">Your cart</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
          {items.map((i) => (
              <div key={i.key || i.id} className="flex gap-4 bg-white border border-black/5 rounded-2xl p-4">
              <img src={i.image} alt={i.name} className="w-24 h-24 rounded-xl object-cover bg-[#E7E1B1]" />
              <div className="flex-1">
                <h3 className="font-display text-lg leading-tight">{i.name}</h3>
                <Price ghs={i.delivered_ghs} gbp={i.delivered_gbp} size="sm" className="mt-1" />
                <div className="flex items-center gap-3 mt-3">
                  <div className="flex items-center border border-black/10 rounded-full">
                    <button onClick={() => setQty(i.key || i.id, i.quantity - 1)} className="w-8 h-8">−</button>
                    <span className="w-7 text-center text-sm font-semibold">{i.quantity}</span>
                    <button onClick={() => setQty(i.key || i.id, i.quantity + 1)} className="w-8 h-8">+</button>
                  </div>
                  <button data-testid={`remove-${i.id}`} onClick={() => remove(i.key || i.id)} className="text-[#5C5C5C] hover:text-[#306D29] flex items-center gap-1 text-sm">
                    <Trash2 size={15} /> Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-[#0D530E] text-white rounded-3xl p-7 h-fit" data-testid="cart-summary">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-white/40 mb-5">Order summary</p>

          <div className="flex items-center justify-between py-2.5 text-sm border-b border-white/10">
            <span className="text-white/70">Products</span>
            <span className="font-medium" data-testid="cart-product-total">{quote ? gh(quote.product_total_ghs) : "—"}</span>
          </div>
          <div className="flex items-center justify-between py-2.5 text-sm border-b border-white/10">
            <span className="text-white/70">AFRIGATE Service Fee</span>
            <span className="font-medium" data-testid="cart-service">{!quote ? "—" : quote.service_quote ? "Quote" : gh(quote.service_ghs)}</span>
          </div>
          <div className="flex items-center justify-between py-2.5 text-sm border-b border-white/10">
            <span className="text-white/70">Shipping (est. · set at checkout)</span>
            <span className="font-medium" data-testid="cart-shipping">{!quote ? "—" : quote.shipping_quote ? "Quote" : gh(quote.shipping_ghs)}</span>
          </div>
          {applied && (
            <div className="flex items-center justify-between py-2.5 text-sm border-b border-white/10" data-testid="cart-discount">
              <span className="text-[#E7E1B1] flex items-center gap-1"><Tag size={13} /> {quote.promo_code}</span>
              <span className="font-medium text-[#E7E1B1]">− {gh(quote.discount_ghs)}</span>
            </div>
          )}

          {/* Promo code */}
          <div className="mt-4">
            <div className="flex gap-2">
              <input value={promoInput} onChange={(e) => setPromoInput(e.target.value.toUpperCase())} placeholder="Promo code" data-testid="cart-promo-input"
                className="flex-1 bg-white/10 border border-white/15 rounded-full px-4 py-2 text-sm placeholder-white/40 focus:outline-none focus:ring-1 focus:ring-[#E7E1B1]" />
              <button type="button" onClick={() => setPromo(promoInput.trim())} data-testid="cart-promo-apply"
                className="bg-[#E7E1B1] text-[#0D530E] font-semibold px-4 py-2 rounded-full text-sm">Apply</button>
            </div>
            {promo && quote && quote.promo_message && !applied && <p className="text-xs text-[#F2C6C6] mt-2" data-testid="cart-promo-msg">{quote.promo_message}</p>}
            {applied && <p className="text-xs text-[#E7E1B1] mt-2 flex items-center gap-1"><Check size={12} /> Promo applied</p>}
          </div>

          <div className="flex items-center justify-between pt-4 mt-2 border-t border-white/10">
            <span className="text-white/70">Total</span>
            <span className="font-display text-2xl font-semibold" data-testid="cart-total">{!quote ? "—" : quote.needs_quote ? "Quote" : gh(quote.total_ghs)}</span>
          </div>
          <p className="text-right text-white/50 text-sm mt-1">{quote && !quote.needs_quote ? `Three simple numbers` : quote?.needs_quote ? "Contact us for a quote" : ""}</p>

          <p className="flex items-start gap-2 text-xs text-white/50 mt-4">
            <Info size={14} className="mt-0.5 shrink-0" />
            No markup. You pay the exact UK price plus our transparent shopping fee &amp; weight-based shipping.
          </p>
          <button data-testid="checkout-btn" onClick={() => navigate("/checkout", { state: { promo } })}
            className="w-full mt-6 bg-[#306D29] hover:bg-[#0D530E] text-white font-semibold py-3.5 rounded-full flex items-center justify-center gap-2"
            style={{ transition: "background-color 0.2s ease" }}>
            Proceed to checkout <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
